import { GameSettings, Student } from '@/common/types/student';
import { StudentEventType } from '@/common/types/studentEvents';
import { resetLevelProgress } from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { getStudentHasMasteredLevel } from '@/common/utils/student';
import { getSettingsChanges } from '@/common/utils/studentEvents';
import api from '@/db/apiClient';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getCharacterInventory,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';

export class StudentManager {
  static _instance?: StudentManager;

  constructor() {
    if (StudentManager.instance !== undefined) {
      return;
    }

    StudentManager._instance = this;

    // Event listeners. These need to be registered directly to the event bus because they are not part of a scene.
    EventBus.on(
      GAME_EVENTS.ENTERED_NEW_BIOME,
      this.handlePlayerEnteredNewBiome,
      this,
    );
    EventBus.on(
      GAME_EVENTS.MASTERED_NEW_MINIGAME,
      this.handleMasteredNewMinigame,
      this,
    );
    EventBus.on(
      GAME_EVENTS.CHARACTER_CREATION_COMPLETED,
      this.handleCharacterCreationCompleted,
      this,
    );
    EventBus.on(
      GAME_EVENTS.GENERAL_GAME_STATE_UPDATED_AFTER_LEVEL_COMPLETE,
      this.handleCompletedCurrentLevel,
      this,
    );
    EventBus.on(
      GAME_EVENTS.PLAYER_VIEWED_LEVEL_LESSON,
      this.handlePlayerViewedLevelLesson,
      this,
    );
    EventBus.on(
      GAME_EVENTS.PLAYER_VIEWED_HUD_WALKTHROUGH,
      this.handlePlayerViewedHudWalkthrough,
      this,
    );
    EventBus.on(
      GAME_EVENTS.PLAYER_VIEWED_DUEL_WALKTHROUGH,
      this.handlePlayerViewedDuelWalkthrough,
      this,
    );
    EventBus.on(
      GAME_EVENTS.COMPLETED_ASSESSMENT,
      this.handleCompletedAssessment,
      this,
    );
  }

  static get instance(): StudentManager {
    return StudentManager._instance!;
  }

  static destroyStudentManager() {
    StudentManager._instance = undefined;
  }

  // State updates.
  levelUp(student: Student) {
    const organisationId = student.organisationId;
    const level = student.level;
    const newLevel = level + 1;

    trackMixpanelEvent('Student leveled up', {
      newLevel: newLevel,
      organisationId,
    });

    try {
      this.updateStudentLevel(student, newLevel, false);
      GameInstance.instance.updateGeneralGameState({
        progress: 0,
      });

      EventBus.emit(GAME_EVENTS.STUDENT_LEVEL_UP);
      return { success: true, newLevel };
    } catch (error) {
      console.error('Error updating level:', error);
      return { success: false, error };
    }
  }

  updateStudentLevel(student: Student, level: number, resetProgress: boolean) {
    try {
      if (!student.id) {
        throw new Error('student not found');
      }
      GameInstance.instance.updateStudent({
        ...student,
        level,
        levelsProgress: resetProgress
          ? resetLevelProgress(level, student.levelsProgress)
          : student.levelsProgress,
      });
    } catch (error) {
      console.error('Failed to update organisation student level:', error);
    }
  }

  markBiomeVisited(biome: number) {
    const biomesVisited =
      GameInstance.instance.student.tutorialization.biomesVisited;
    if (biomesVisited.indexOf(biome) === -1) {
      GameInstance.instance.updateStudent({
        tutorialization: {
          ...GameInstance.instance.student.tutorialization,
          biomesVisited: [...biomesVisited, biome],
        },
      });
    }
  }

  resetLastMasteredGame() {
    GameInstance.instance.updateStudent({
      lastMasteredGame: null,
    });
  }

  markHudWalkthroughViewed() {
    GameInstance.instance.updateStudent({
      tutorialization: {
        ...GameInstance.instance.student.tutorialization,
        hasViewedHudWalkthrough: true,
      },
    });
  }

  markDuelWalkthroughViewed() {
    GameInstance.instance.updateStudent({
      tutorialization: {
        ...GameInstance.instance.student.tutorialization,
        hasViewedDuelWalkthrough: true,
      },
    });
  }

  completeOnboarding() {
    GameInstance.instance.updateStudent({
      onboardingCompleted: true,
    });

    const character = getCharacterInventory();
    trackMixpanelEvent('Character Created', {
      character: character,
    });
  }

  async handleCompletedAssessment(placementLevel: number) {
    const student = getStudent();
    const studentId = student.id;
    const assessmentId = student.assessmentId;
    if (assessmentId) {
      try {
        await api.asStudent.markAssessmentCompleted(
          assessmentId,
          studentId,
          placementLevel,
        );
      } catch (e) {
        console.error('Error marking assessment completed:', e);
      }
    }

    const assessmentStudent =
      GameInstance.instance.assessment?.students[studentId];
    const placeAtAssessedLevel =
      assessmentStudent?.placeAtAssessedLevel ?? false;

    if (placeAtAssessedLevel) {
      GameInstance.instance.updateStudent({
        assessmentId: null,
        level: placementLevel,
      });
    } else {
      GameInstance.instance.updateStudent({ assessmentId: null });
    }
    GameInstance.instance.assessment = null;

    // Mixpanel event tracking
    trackMixpanelEvent('User Completed Assessment', {
      assessmentId,
    });
    EventBus.emit(GAME_EVENTS.PLAYER_UPDATED_POST_ASSESSMENT);
  }

  markPlayerViewedLevelInstructions(level: number) {
    GameInstance.instance.updateStudent({
      tutorialization: {
        ...GameInstance.instance.student.tutorialization,
        instructionsViewed: [
          ...GameInstance.instance.student.tutorialization.instructionsViewed,
          level,
        ],
      },
    });
  }

  async updateSettings(newSettings: GameSettings) {
    const {
      id: studentId,
      organisationId,
      settings: currentSettings,
    } = getStudent();
    if (!studentId) {
      console.error('No student ID found');
      return;
    }

    const currentSessionId = getSession().currentSession?.id;
    if (!currentSessionId) {
      console.error('No session found');
      return;
    }

    const changes = getSettingsChanges(currentSettings, newSettings);
    if (!changes.length) return;

    try {
      await api.asStudent.addStudentEvent({
        type: StudentEventType.SETTING_CHANGED,
        organisationId,
        studentId,
        actor: { type: 'student' },
        nestedUnderSessionId: currentSessionId,
        createdAt: Date.now(),
        changes,
        location: 'game',
      });
    } catch (e) {
      console.error('Error adding student event:', e);
    }

    GameInstance.instance.updateStudent({
      settings: {
        ...currentSettings,
        ...newSettings,
        audio: {
          ...currentSettings.audio,
          ...newSettings.audio,
        },
      },
    });
  }

  // Event handlers.
  handlePlayerEnteredNewBiome(biome: number) {
    this.markBiomeVisited(biome);
  }

  handleMasteredNewMinigame() {
    this.resetLastMasteredGame();
  }

  handlePlayerViewedHudWalkthrough() {
    this.markHudWalkthroughViewed();
  }

  handlePlayerViewedDuelWalkthrough() {
    this.markDuelWalkthroughViewed();
  }

  handleCharacterCreationCompleted() {
    this.completeOnboarding();
  }

  handleCompletedCurrentLevel() {
    const student = getStudent();
    const level = student.level;

    // Only level up if the player has achieved mastery.
    if (getStudentHasMasteredLevel(student, level)) {
      this.levelUp(student);
    }

    GameInstance.instance.updateStudent({
      mapsCompleted: getStudent().mapsCompleted + 1,
    });
  }

  handlePlayerViewedLevelLesson(level: number) {
    this.markPlayerViewedLevelInstructions(level);
  }
}

export const studentManager = new StudentManager();
